<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

export default {
  name: 'AiAlerts',
  components: {
    PaginationFooter,
    ConfirmationModal,
  },
  setup() {
    const { formatMessage } = useMessageFormatter();
    return { formatMessage };
  },
  data() {
    return {
      alerts: [],
      meta: {},
      loading: false,
      isUpdatingSettings: false,
      positiveWords: [],
      negativeWords: [],
      brandShieldEnabled: false,
      autoLikeEnabled: false,
      brandShieldFacebookEnabled: false,
      autoLikeFacebookEnabled: false,
      showConversationModal: false,
      showImageModal: false,
      currentImageUrl: '',
      conversationMessages: [],
      isLoadingConversation: false,
      activeTab: 'alerts',
      reports: [],
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentRole: 'getCurrentRole',
    }),
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    currentAccount() {
      return this.getAccount(this.$route.params.accountId) || {};
    },
    validAlerts() {
      if (!Array.isArray(this.alerts)) return [];
      return this.alerts.filter(a => a && a.id);
    },
    groupedAlerts() {
      const groups = {};
      this.validAlerts.forEach(alert => {
        const cid = alert.conversation_id || alert.conversation?.id;
        if (!groups[cid]) {
          groups[cid] = {
            conversation: alert.conversation,
            alerts: [],
            created_at: alert.created_at,
          };
        }
        groups[cid].alerts.push(alert);
      });
      return Object.values(groups).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    },
  },
  watch: {
    'currentAccount.id'(id) {
      if (id) {
        this.initSettings();
      }
    },
  },
  mounted() {
    if (this.$route.query.tab === 'reports') {
      this.activeTab = 'reports';
    }
    this.fetchAlerts();
    this.fetchReports();
    this.initSettings();
  },
  methods: {
    formatActivityMessage(content) {
      if (!content) return '';
      return content.replace(/<\/?[^>]+(>|$)/g, '');
    },
    async fetchConversation(displayId) {
      this.isLoadingConversation = true;
      try {
        const response = await window.axios.get(
          `/api/v1/accounts/${this.$route.params.accountId}/conversations/${displayId}/messages`
        );
        this.conversationMessages = response.data.payload || [];
      } catch (error) {
        useAlert(this.$t('AI_ALERTS.ALERTS.CONVERSATION_LOAD_ERROR'));
      } finally {
        this.isLoadingConversation = false;
      }
    },
    openImage(url) {
      if (url) {
        this.currentImageUrl = url;
        this.showImageModal = true;
      }
    },
    openConversationModal(displayId) {
      this.showConversationModal = true;
      this.conversationMessages = []; // clear old messages
      this.fetchConversation(displayId);
    },
    openConversation(conversation) {
      if (conversation.tickets && conversation.tickets.length > 0) {
        const ticket = conversation.tickets[conversation.tickets.length - 1];
        this.$router.push({
          name: 'tickets_dashboard_details',
          params: {
            accountId: this.$route.params.accountId,
            ticketId: ticket.id,
          },
        });
      } else {
        this.$router.push({
          name: 'inbox_conversation',
          params: {
            accountId: this.$route.params.accountId,
            conversation_id: conversation.display_id,
          },
        });
      }
    },
    initSettings() {
      const customAttributes = this.currentAccount.custom_attributes || {};
      const pos = customAttributes.lumerai_positive_trigger_words || '';
      const neg = customAttributes.lumerai_negative_trigger_words || '';

      this.brandShieldEnabled =
        customAttributes.lumerai_brand_shield_enabled === 'true';
      this.autoLikeEnabled =
        customAttributes.lumerai_auto_like_enabled === 'true';
      this.brandShieldFacebookEnabled =
        customAttributes.lumerai_brand_shield_fb_enabled === 'true';
      this.autoLikeFacebookEnabled =
        customAttributes.lumerai_auto_like_fb_enabled === 'true';

      this.positiveWords = pos
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .slice(0, 3);
      this.negativeWords = neg
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .slice(0, 3);

      if (this.positiveWords.length === 0) this.positiveWords.push('');
      if (this.negativeWords.length === 0) this.negativeWords.push('');
    },
    addPositiveWord() {
      if (this.positiveWords.length < 3) this.positiveWords.push('');
    },
    cleanPositiveWord(index) {
      this.positiveWords[index] = this.positiveWords[index].replace(
        /[^a-zA-Z\u00C0-\u00FF\s]/g,
        ''
      );
    },
    removePositiveWord(index) {
      this.positiveWords.splice(index, 1);
    },
    addNegativeWord() {
      if (this.negativeWords.length < 3) this.negativeWords.push('');
    },
    cleanNegativeWord(index) {
      this.negativeWords[index] = this.negativeWords[index].replace(
        /[^a-zA-Z\u00C0-\u00FF\s]/g,
        ''
      );
    },
    removeNegativeWord(index) {
      this.negativeWords.splice(index, 1);
    },
    async saveSettings() {
      this.isUpdatingSettings = true;
      try {
        const posWords = this.positiveWords
          .map(w => w.replace(/[^a-zA-Z\u00C0-\u00FF\s]/g, '').trim())
          .filter(Boolean)
          .join(',');
        const negWords = this.negativeWords
          .map(w => w.replace(/[^a-zA-Z\u00C0-\u00FF\s]/g, '').trim())
          .filter(Boolean)
          .join(',');

        await this.$store.dispatch('accounts/update', {
          custom_attributes: {
            ...this.currentAccount.custom_attributes,
            lumerai_positive_trigger_words: posWords,
            lumerai_negative_trigger_words: negWords,
            lumerai_brand_shield_enabled: this.brandShieldEnabled
              ? 'true'
              : 'false',
            lumerai_auto_like_enabled: this.autoLikeEnabled ? 'true' : 'false',
            lumerai_brand_shield_fb_enabled: this.brandShieldFacebookEnabled
              ? 'true'
              : 'false',
            lumerai_auto_like_fb_enabled: this.autoLikeFacebookEnabled
              ? 'true'
              : 'false',
          },
        });
        useAlert(this.$t('AI_ALERTS.ALERTS.SETTINGS_SAVED'));
      } catch (error) {
        useAlert(this.$t('AI_ALERTS.ALERTS.TRIGGER_WORDS_SAVE_ERROR'));
      } finally {
        this.isUpdatingSettings = false;
      }
    },
    async fetchAlerts(page = 1) {
      this.loading = true;
      try {
        const response = await window.axios.get(
          `/api/v1/accounts/${this.$route.params.accountId}/lumerai_alerts`,
          { params: { page } }
        );
        if (Array.isArray(response.data)) {
          this.alerts = response.data;
        } else if (response.data && Array.isArray(response.data.payload)) {
          this.alerts = response.data.payload;
          this.meta = response.data.meta || {};
        } else {
          this.alerts = [];
        }
      } catch (error) {
        useAlert(this.$t('AI_ALERTS.ALERTS.LOAD_ERROR'));
      } finally {
        this.loading = false;
      }
    },
    async fetchReports() {
      try {
        const response = await window.axios.get(
          `/api/v1/accounts/${this.$route.params.accountId}/lumerai_alerts/report`
        );
        this.reports = response.data.payload || {};
      } catch (error) {
        // fail silently for reports
      }
    },
    async resolveAlerts(conversationId) {
      const isConfirmed = await this.$refs.confirmDialog.showConfirmation();
      if (!isConfirmed) return;

      try {
        await window.axios.post(
          `/api/v1/accounts/${this.$route.params.accountId}/lumerai_alerts/resolve_by_conversation`,
          { conversation_id: conversationId }
        );
        useAlert(this.$t('AI_ALERTS.ALERTS.RESOLVED'));
        this.fetchAlerts();
        this.fetchReports();
      } catch (error) {
        useAlert(this.$t('AI_ALERTS.ALERTS.RESOLVE_ERROR'));
      }
    },
    async reviewAttachment(attachmentId, decision) {
      try {
        await window.axios.post(
          `/api/v1/accounts/${this.$route.params.accountId}/lumerai_alerts/review_attachment`,
          { attachment_id: attachmentId, decision }
        );
        useAlert(
          decision === 'approve'
            ? this.$t('AI_ALERTS.ALERTS.IMAGE_APPROVED')
            : this.$t('AI_ALERTS.ALERTS.IMAGE_KEPT_HIDDEN')
        );
        this.fetchAlerts();
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            this.$t('AI_ALERTS.ALERTS.IMAGE_REVIEW_ERROR')
        );
      }
    },
    formatType(type) {
      const types = {
        anger: 'AI_ALERTS_PAGE.SENTIMENT.ANGER',
        compliment: 'AI_ALERTS_PAGE.SENTIMENT.COMPLIMENT',
        bad_word: 'AI_ALERTS_PAGE.SENTIMENT.BAD_WORD',
        bad_mood: 'AI_ALERTS_PAGE.SENTIMENT.BAD_MOOD',
        neutral: 'AI_ALERTS_PAGE.SENTIMENT.NEUTRAL',
        unsafe_content: 'AI_ALERTS_PAGE.SENTIMENT.UNSAFE_CONTENT',
      };
      // type is looked up in the fixed sentiment translation map above.
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return types[type] ? this.$t(types[type]) : type;
    },
    emojiForType(type) {
      if (type === 'unsafe_content') return '⚠️';
      if (type === 'anger') return '😡';
      if (type === 'bad_word') return '🤬';
      if (type === 'bad_mood') return '😠';
      if (type === 'compliment') return '😍';
      return '🤖';
    },
    borderClass(type) {
      if (type === 'unsafe_content')
        return 'border-red-400 hover:border-red-500';
      if (type === 'anger' || type === 'bad_word' || type === 'bad_mood')
        return 'border-red-200 hover:border-red-300';
      if (type === 'compliment')
        return 'border-green-200 hover:border-green-300';
      return 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:border-slate-600';
    },
    badgeClass(type) {
      if (type === 'unsafe_content') return 'bg-red-100 text-red-900';
      if (type === 'anger' || type === 'bad_word' || type === 'bad_mood')
        return 'bg-red-100 text-red-800';
      if (type === 'compliment') return 'bg-green-100 text-green-800';
      return 'bg-slate-100 text-slate-800 dark:text-slate-100';
    },
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, vue/no-static-inline-styles, vue/html-self-closing -->
  <div
    class="flex flex-col flex-1 h-full min-w-0 bg-n-background dark:bg-slate-900 p-8 overflow-y-auto w-full"
  >
    <div class="flex items-center justify-between mb-8">
      <div>
        <h1
          class="text-3xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2"
        >
          <fluent-icon
            icon="bot"
            size="32"
            class="text-purple-600 dark:text-purple-400"
          />
          <span>{{ $t('AI_ALERTS.TITLE') }}</span>
        </h1>
        <p class="text-slate-500 dark:text-slate-400 dark:text-slate-500 mt-2">
          {{ $t('AI_ALERTS.DESCRIPTION') }}
        </p>
      </div>
      <button
        class="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:bg-slate-700 transition-colors shadow-sm"
        @click="fetchAlerts"
      >
        <fluent-icon icon="arrow-clockwise" size="16" />
        {{ $t('AI_ALERTS.REFRESH') }}
      </button>
    </div>

    <div
      class="mb-6 flex gap-4 border-b border-slate-200 dark:border-slate-700"
    >
      <button
        class="py-3 px-6 font-medium text-sm transition-all outline-none"
        :class="
          activeTab === 'alerts'
            ? 'border-b-2 border-woot-500 text-woot-600 dark:text-woot-500'
            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
        "
        @click="activeTab = 'alerts'"
      >
        {{ $t('AI_ALERTS.PENDING_ALERTS') }}
      </button>
      <button
        class="py-3 px-6 font-medium text-sm transition-all outline-none"
        :class="
          activeTab === 'reports'
            ? 'border-b-2 border-woot-500 text-woot-600 dark:text-woot-500'
            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
        "
        @click="activeTab = 'reports'"
      >
        {{ $t('AI_ALERTS.AGENT_REPORT') }}
      </button>
      <button
        class="py-3 px-6 font-medium text-sm transition-all outline-none"
        :class="
          activeTab === 'settings'
            ? 'border-b-2 border-woot-500 text-woot-600 dark:text-woot-500'
            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
        "
        @click="activeTab = 'settings'"
      >
        {{ $t('AI_ALERTS.SETTINGS') }}
      </button>
    </div>

    <div
      v-if="activeTab === 'settings'"
      class="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 mb-8"
    >
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100">
          {{ $t('AI_ALERTS.TRIGGER_WORDS_TITLE') }}
        </h2>
        <button
          :disabled="isUpdatingSettings"
          class="flex items-center gap-2 px-4 py-2 bg-woot-500 text-white rounded-md text-sm font-medium hover:bg-woot-600 transition-colors shadow-sm disabled:opacity-50"
          @click="saveSettings"
        >
          {{ $t('AI_ALERTS.SAVE_CHANGES') }}
        </button>
      </div>
      <p
        class="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 mb-6"
      >
        {{ $t('AI_ALERTS.TRIGGER_WORDS_DESCRIPTION') }}
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Positive Words -->
        <div>
          <div class="flex justify-between items-center mb-3">
            <label
              class="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2"
              >{{ $t('AI_ALERTS.PRAISE') }}</label
            >
          </div>
          <div class="flex flex-col gap-2">
            <div
              v-for="(word, index) in positiveWords"
              :key="'pos' + index"
              class="flex gap-2"
            >
              <input
                v-model="positiveWords[index]"
                type="text"
                maxlength="30"
                class="flex-1 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-sm"
                :placeholder="$t('AI_ALERTS.POSITIVE_WORD_EXAMPLE')"
                @input="cleanPositiveWord(index)"
              />
              <button
                type="button"
                class="text-red-500 hover:bg-red-50 dark:hover:bg-red-900 p-2 rounded flex items-center"
                @click="removePositiveWord(index)"
              >
                <fluent-icon icon="delete" size="16" />
              </button>
            </div>
            <button
              v-if="positiveWords.length < 3"
              type="button"
              class="text-woot-600 font-medium text-sm text-left mt-1 hover:underline w-fit"
              @click="addPositiveWord"
            >
              {{ $t('AI_ALERTS.ADD_WORD') }}
            </button>
          </div>
        </div>

        <!-- Negative Words -->
        <div>
          <div class="flex justify-between items-center mb-3">
            <label
              class="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2"
              >{{ $t('AI_ALERTS.ANGER') }}</label
            >
          </div>
          <div class="flex flex-col gap-2">
            <div
              v-for="(word, index) in negativeWords"
              :key="'neg' + index"
              class="flex gap-2"
            >
              <input
                v-model="negativeWords[index]"
                type="text"
                maxlength="30"
                class="flex-1 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-sm"
                :placeholder="$t('AI_ALERTS.NEGATIVE_WORD_EXAMPLE')"
                @input="cleanNegativeWord(index)"
              />
              <button
                type="button"
                class="text-red-500 hover:bg-red-50 dark:hover:bg-red-900 p-2 rounded flex items-center"
                @click="removeNegativeWord(index)"
              >
                <fluent-icon icon="delete" size="16" />
              </button>
            </div>
            <button
              v-if="negativeWords.length < 3"
              type="button"
              class="text-woot-600 font-medium text-sm text-left mt-1 hover:underline w-fit"
              @click="addNegativeWord"
            >
              {{ $t('AI_ALERTS.ADD_WORD') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Superpowers Toggles -->
      <div class="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
        <h3
          class="text-md font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
        >
          {{ $t('AI_ALERTS.INSTAGRAM_TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {{ $t('AI_ALERTS.INSTAGRAM_DESCRIPTION') }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              v-model="autoLikeEnabled"
              type="checkbox"
              class="form-checkbox mt-1 h-5 w-5 text-woot-500 rounded border-slate-300 focus:ring-woot-500"
            />
            <div class="flex flex-1 flex-col gap-2">
              <span class="font-semibold text-slate-700 dark:text-slate-200">{{
                $t('AI_ALERTS.AUTO_LIKE_PRAISE')
              }}</span>
              <span class="text-sm text-slate-500">{{
                $t('AI_ALERTS_PAGE.INSTAGRAM_LIKE_DESCRIPTION')
              }}</span>
            </div>
          </label>

          <label class="flex items-center gap-3 cursor-pointer">
            <input
              v-model="brandShieldEnabled"
              type="checkbox"
              class="form-checkbox h-5 w-5 text-woot-500 rounded border-slate-300 focus:ring-woot-500"
            />
            <div class="flex flex-col">
              <span class="font-semibold text-slate-700 dark:text-slate-200">{{
                $t('AI_ALERTS.BRAND_SHIELD')
              }}</span>
              <span class="text-sm text-slate-500">{{
                $t('AI_ALERTS_PAGE.INSTAGRAM_SHIELD_DESCRIPTION')
              }}</span>
            </div>
          </label>
        </div>
      </div>

      <!-- Facebook Superpowers Toggles -->
      <div class="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
        <h3
          class="text-md font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2"
        >
          {{ $t('AI_ALERTS_PAGE.FACEBOOK_SUPERPOWERS_TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {{ $t('AI_ALERTS_PAGE.FACEBOOK_SUPERPOWERS_DESCRIPTION') }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              v-model="autoLikeFacebookEnabled"
              type="checkbox"
              class="form-checkbox mt-1 h-5 w-5 text-woot-500 rounded border-slate-300 focus:ring-woot-500"
            />
            <div class="flex flex-1 flex-col gap-2">
              <span class="font-semibold text-slate-700 dark:text-slate-200">{{
                $t('AI_ALERTS_PAGE.FACEBOOK_LIKE_TITLE')
              }}</span>
              <span class="text-sm text-slate-500">{{
                $t('AI_ALERTS_PAGE.FACEBOOK_LIKE_DESCRIPTION')
              }}</span>
            </div>
          </label>

          <label class="flex items-center gap-3 cursor-pointer">
            <input
              v-model="brandShieldFacebookEnabled"
              type="checkbox"
              class="form-checkbox h-5 w-5 text-woot-500 rounded border-slate-300 focus:ring-woot-500"
            />
            <div class="flex flex-col">
              <span class="font-semibold text-slate-700 dark:text-slate-200">{{
                $t('AI_ALERTS_PAGE.FACEBOOK_SHIELD_TITLE')
              }}</span>
              <span class="text-sm text-slate-500">{{
                $t('AI_ALERTS_PAGE.FACEBOOK_SHIELD_DESCRIPTION')
              }}</span>
            </div>
          </label>
        </div>
      </div>
    </div>

    <div
      v-if="activeTab === 'reports'"
      class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-750 overflow-hidden"
    >
      <div class="p-6">
        <h3 class="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2">
          {{ $t('AI_ALERTS_PAGE.REPORT_TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {{ $t('AI_ALERTS_PAGE.REPORT_DESCRIPTION') }}
        </p>

        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead
              class="text-xs text-slate-500 bg-slate-50 dark:bg-slate-700 uppercase"
            >
              <tr>
                <th class="px-6 py-3 font-semibold">
                  {{ $t('AI_ALERTS_PAGE.AGENT') }}
                </th>
                <th class="px-6 py-3 font-semibold text-center">
                  {{ $t('AI_ALERTS_PAGE.TOTAL') }}
                </th>
                <th class="px-6 py-3 font-semibold text-center">
                  {{ $t('AI_ALERTS_PAGE.PRAISE') }}
                </th>
                <th class="px-6 py-3 font-semibold text-center">
                  {{ $t('AI_ALERTS_PAGE.ANGER') }}
                </th>
                <th class="px-6 py-3 font-semibold text-center">
                  {{ $t('AI_ALERTS_PAGE.BAD_MOOD') }}
                </th>
                <th class="px-6 py-3 font-semibold text-center">
                  {{ $t('AI_ALERTS_PAGE.PROFANITY') }}
                </th>
                <th class="px-6 py-3 font-semibold text-center">
                  {{ $t('AI_ALERTS_PAGE.SENSITIVE_CONTENT') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="Object.keys(reports).length === 0">
                <td colspan="7" class="px-6 py-8 text-center text-slate-500">
                  {{ $t('AI_ALERTS_PAGE.NO_REPORT_DATA') }}
                </td>
              </tr>
              <tr
                v-for="(data, assignee) in reports"
                :key="assignee"
                class="border-b border-slate-100 dark:border-slate-700"
              >
                <td
                  class="px-6 py-4 font-medium text-slate-800 dark:text-slate-200"
                >
                  {{ assignee }}
                </td>
                <td class="px-6 py-4 text-center font-bold">
                  {{ data.total }}
                </td>
                <td class="px-6 py-4 text-center text-green-600">
                  {{ data.compliment }}
                </td>
                <td class="px-6 py-4 text-center text-red-600">
                  {{ data.anger }}
                </td>
                <td class="px-6 py-4 text-center text-orange-600">
                  {{ data.bad_mood }}
                </td>
                <td class="px-6 py-4 text-center text-purple-600">
                  {{ data.bad_word }}
                </td>
                <td class="px-6 py-4 text-center text-blue-600">
                  {{ data.unsafe_content }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template v-else-if="activeTab === 'alerts'">
      <div v-if="loading" class="flex justify-center py-12">
        <woot-spinner size="" />
      </div>

      <div
        v-else-if="groupedAlerts.length === 0"
        class="flex flex-col items-center justify-center py-20 text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-750"
      >
        <fluent-icon
          icon="checkmark-circle"
          size="48"
          class="text-green-400 mb-4"
        />
        <span class="text-lg font-medium text-slate-700 dark:text-slate-200">{{
          $t('AI_ALERTS_PAGE.EMPTY_TITLE')
        }}</span>
        <span class="text-sm mt-1">{{
          $t('AI_ALERTS_PAGE.EMPTY_DESCRIPTION')
        }}</span>
      </div>

      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8"
      >
        <div
          v-for="group in groupedAlerts"
          :key="
            'group-' +
            (group.conversation ? group.conversation.id : Math.random())
          "
          class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border-2 overflow-hidden hover:shadow-md transition-all flex flex-col"
          :class="borderClass(group.alerts[0].alert_type)"
        >
          <div class="p-5 flex-1">
            <div class="flex justify-between items-start mb-4">
              <div class="flex items-center gap-3">
                <span class="text-4xl filter drop-shadow-sm">{{
                  emojiForType(group.alerts[0].alert_type)
                }}</span>
                <div>
                  <span
                    :class="badgeClass(group.alerts[0].alert_type)"
                    class="px-2.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider"
                  >
                    {{ formatType(group.alerts[0].alert_type) }}
                    <span v-if="group.alerts.length > 1">
                      {{ $t('COMMON.LEFT_PAREN') }}{{ group.alerts.length
                      }}{{ $t('COMMON.RIGHT_PAREN') }}</span
                    >
                  </span>
                  <div
                    class="text-xs text-slate-400 dark:text-slate-500 mt-1.5 font-medium flex items-center gap-1"
                  >
                    {{ $t('AI_ALERTS_PAGE.LAST_ALERT') }}
                    {{ new Date(group.created_at).toLocaleString() }}
                  </div>
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <div
                v-for="alert in group.alerts"
                :key="alert.id"
                class="text-slate-700 dark:text-slate-200 text-sm leading-relaxed p-3.5 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-100 dark:border-slate-750 relative"
              >
                <span class="block">{{ alert.message }}</span>
              </div>
            </div>
          </div>

          <div
            class="bg-slate-50 dark:bg-slate-700 border-t border-slate-100 dark:border-slate-750 p-4 flex flex-col gap-3"
          >
            <div class="flex justify-between items-center">
              <div
                v-if="group.conversation"
                class="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1.5 w-full"
              >
                <template
                  v-if="
                    group.conversation.tickets &&
                    group.conversation.tickets.length > 0
                  "
                >
                  <div class="flex items-center justify-between w-full">
                    <div class="flex items-center gap-1.5">
                      <fluent-icon
                        icon="tag"
                        size="16"
                        class="text-slate-400 dark:text-slate-500"
                      />
                      {{ $t('AI_ALERTS_PAGE.TICKET') }}
                      <span class="text-slate-800 dark:text-slate-100"
                        >{{ $t('COMMON.HASH_PREFIX')
                        }}{{
                          group.conversation.tickets[
                            group.conversation.tickets.length - 1
                          ].id
                        }}</span
                      >
                    </div>
                    <div
                      class="flex items-center gap-1.5 text-slate-500 dark:text-slate-400"
                    >
                      <fluent-icon icon="chat" size="16" />
                      {{ $t('AI_ALERTS_PAGE.CONVERSATION') }}
                      <span class="text-slate-700 dark:text-slate-200"
                        >{{ $t('COMMON.HASH_PREFIX')
                        }}{{ group.conversation.display_id }}</span
                      >
                    </div>
                  </div>
                </template>
                <template v-else>
                  <fluent-icon
                    icon="chat"
                    size="16"
                    class="text-slate-400 dark:text-slate-500"
                  />
                  {{ $t('AI_ALERTS_PAGE.CONVERSATION') }}
                  <span class="text-slate-800 dark:text-slate-100"
                    >{{ $t('COMMON.HASH_PREFIX')
                    }}{{ group.conversation.display_id }}</span
                  >
                </template>
              </div>
              <div
                v-else
                class="text-sm text-slate-400 dark:text-slate-500 italic"
              >
                {{ $t('AI_ALERTS_PAGE.CONVERSATION_NOT_FOUND') }}
              </div>
            </div>

            <div
              v-if="
                group.alerts[0].source_message &&
                group.alerts[0].source_message.content_attributes &&
                group.alerts[0].source_message.content_attributes
                  .is_instagram_comment
              "
              class="flex flex-col gap-3 mt-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 rounded-lg shadow-sm"
            >
              <div
                class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1"
              >
                <fluent-icon icon="camera" size="14" />
                {{ $t('AI_ALERTS_PAGE.INSTAGRAM_COMMENT') }}
              </div>
              <div class="flex items-start gap-4">
                <div
                  v-if="
                    group.alerts[0].source_message.content_attributes.image_url
                  "
                  class="flex-shrink-0"
                >
                  <img
                    :src="
                      group.alerts[0].source_message.content_attributes
                        .image_url
                    "
                    class="w-16 h-16 rounded object-cover shadow-sm border border-slate-100 dark:border-slate-700"
                  />
                </div>
                <div class="flex flex-col flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <span
                      class="font-bold text-sm text-slate-800 dark:text-slate-200 truncate"
                      >{{
                        group.alerts[0].source_message.sender?.name ||
                        group.alerts[0].source_message.content_attributes
                          .author_name ||
                        group.alerts[0].source_message.content_attributes
                          .ig_username ||
                        $t('AI_ALERTS_PAGE.UNKNOWN_INSTAGRAM_USER')
                      }}</span
                    >
                    <span class="text-xs text-slate-400">
                      {{ $t('COMMON.BULLET_SEPARATOR') }}
                    </span>
                    <a
                      v-if="
                        group.alerts[0].source_message.content_attributes
                          .permalink
                      "
                      :href="
                        group.alerts[0].source_message.content_attributes
                          .permalink
                      "
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-blue-500 hover:text-blue-600 text-xs hover:underline flex items-center gap-1"
                    >
                      {{ $t('AI_ALERTS_PAGE.VIEW_ORIGINAL_POST') }}
                      <fluent-icon icon="open" size="12" />
                    </a>
                  </div>
                  <span
                    class="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-words italic"
                    >{{ $t('COMMON.DOUBLE_QUOTE')
                    }}{{ group.alerts[0].source_message.content
                    }}{{ $t('COMMON.DOUBLE_QUOTE') }}</span
                  >
                </div>
              </div>

              <div
                class="flex justify-end mt-2 pt-3 border-t border-slate-100 dark:border-slate-700"
              >
                <button
                  v-tooltip.top-end="$t('AI_ALERTS_PAGE.RESOLVE_ALERT')"
                  class="text-sm text-slate-600 dark:text-slate-300 font-bold hover:text-green-600 dark:hover:text-green-500 flex items-center justify-center gap-1 bg-white dark:bg-slate-800 px-4 py-2 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm transition-colors"
                  @click="resolveAlerts(group.conversation.id)"
                >
                  <fluent-icon icon="checkmark" size="14" />
                  {{ $t('AI_ALERTS_PAGE.RESOLVE_ALERT') }}
                </button>

                <button
                  v-if="group.conversation"
                  class="ml-2 text-sm text-woot-600 font-bold hover:text-woot-700 flex items-center justify-center gap-1 bg-white dark:bg-slate-800 px-4 py-2 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm transition-colors"
                  @click="openConversationModal(group.conversation.display_id)"
                >
                  <fluent-icon icon="eye-show" size="14" />
                  {{ $t('AI_ALERTS_PAGE.VIEW_FULL_CONTEXT') }}
                </button>
              </div>
            </div>
            <div v-else class="flex justify-end gap-2 mt-2">
              <template
                v-if="
                  isAdmin &&
                  group.alerts[0].alert_type === 'unsafe_content' &&
                  group.alerts[0].attachment_id
                "
              >
                <button
                  type="button"
                  class="flex-1 rounded-md border border-red-300 bg-red-50 px-2 py-2 text-sm font-bold text-red-800 hover:bg-red-100"
                  @click="
                    reviewAttachment(
                      group.alerts[0].attachment_id,
                      'keep_blocked'
                    )
                  "
                >
                  {{ $t('AI_ALERTS_PAGE.KEEP_HIDDEN') }}
                </button>
                <button
                  type="button"
                  class="flex-1 rounded-md border border-slate-300 bg-white px-2 py-2 text-sm font-bold text-slate-800 hover:bg-slate-50"
                  @click="
                    reviewAttachment(group.alerts[0].attachment_id, 'approve')
                  "
                >
                  {{ $t('AI_ALERTS_PAGE.APPROVE_IMAGE') }}
                </button>
              </template>
              <button
                v-if="group.conversation"
                v-tooltip.top-end="$t('AI_ALERTS_PAGE.RESOLVE_ALERT')"
                class="w-10 flex-shrink-0 text-sm text-slate-600 dark:text-slate-300 font-bold hover:text-green-600 dark:hover:text-green-500 flex items-center justify-center gap-1 bg-white dark:bg-slate-800 py-2 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm transition-colors"
                @click="resolveAlerts(group.conversation.id)"
              >
                <fluent-icon icon="checkmark" size="14" />
              </button>

              <button
                v-if="group.alerts[0].attachment"
                class="flex-1 text-sm text-woot-600 font-bold hover:text-woot-700 flex items-center justify-center gap-1 bg-white dark:bg-slate-800 px-2 py-2 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm transition-colors"
                @click="openImage(group.alerts[0].attachment.download_url)"
              >
                <fluent-icon icon="image" size="14" />
                {{ $t('AI_ALERTS_PAGE.IMAGE') }}
              </button>
              <button
                v-else-if="group.conversation"
                class="flex-1 text-sm text-woot-600 font-bold hover:text-woot-700 flex items-center justify-center gap-1 bg-white dark:bg-slate-800 px-2 py-2 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm transition-colors"
                @click="openConversationModal(group.conversation.display_id)"
              >
                <fluent-icon icon="eye-show" size="14" />
                {{ $t('AI_ALERTS_PAGE.VIEW') }}
              </button>

              <button
                v-if="group.conversation"
                class="flex-1 text-sm text-blue-600 font-bold hover:text-blue-700 flex items-center justify-center gap-1 bg-white dark:bg-slate-800 px-2 py-2 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm transition-colors"
                @click="openConversation(group.conversation)"
              >
                {{ $t('AI_ALERTS_PAGE.OPEN') }}
                <fluent-icon icon="open" size="14" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="meta.total_count" class="mt-6 flex justify-center">
        <PaginationFooter
          :current-page="Number(meta.current_page)"
          :total-items="meta.total_count"
          :items-per-page="25"
          @update:current-page="fetchAlerts"
        />
      </div>
    </template>

    <!-- Modal Ver Conversa -->
    <woot-modal
      v-model:show="showConversationModal"
      :on-close="() => (showConversationModal = false)"
      class="!max-w-2xl"
    >
      <woot-modal-header
        :header-title="$t('AI_ALERTS_PAGE.ORIGINAL_CONVERSATION')"
      />
      <div
        class="p-6 overflow-y-auto bg-slate-50 dark:bg-slate-900"
        style="max-height: 70vh"
      >
        <div v-if="isLoadingConversation" class="flex justify-center p-4">
          <span class="text-slate-500 text-sm font-medium">{{
            $t('AI_ALERTS_PAGE.LOADING_CONVERSATION')
          }}</span>
        </div>
        <div v-else class="space-y-4">
          <div
            v-for="msg in conversationMessages"
            :key="msg.id"
            class="flex flex-col"
            :class="
              msg.message_type === 0
                ? 'items-start'
                : msg.message_type === 1
                  ? 'items-end'
                  : 'items-center'
            "
          >
            <!-- Mensagem de Sistema / Atividade -->
            <div
              v-if="msg.message_type === 2"
              class="text-[11px] text-slate-500 my-2 text-center bg-slate-200/50 dark:bg-slate-800/50 px-3 py-1 rounded-full w-fit"
            >
              {{ formatActivityMessage(msg.content) }}
              <span class="opacity-70 ml-2"
                >{{ new Date(msg.created_at * 1000).toLocaleDateString() }}
                {{
                  new Date(msg.created_at * 1000).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                }}</span
              >
            </div>

            <!-- Balão de Mensagem Normal -->
            <div
              v-else
              class="max-w-[85%] rounded-2xl px-4 py-2 flex flex-col"
              :class="
                msg.message_type === 0
                  ? 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 rounded-bl-none'
                  : 'bg-woot-500 text-white rounded-br-none'
              "
            >
              <div
                v-dompurify-html="
                  formatMessage(msg.content, false, msg.private)
                "
                class="text-sm whitespace-pre-wrap break-words"
              />
              <!-- Data/Hora da Mensagem -->
              <div
                class="text-[10px] mt-1 flex gap-2"
                :class="
                  msg.message_type === 0
                    ? 'text-slate-400 dark:text-slate-500'
                    : 'text-woot-100/80 justify-end'
                "
              >
                <span
                  >{{ new Date(msg.created_at * 1000).toLocaleDateString() }}
                  {{
                    new Date(msg.created_at * 1000).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </woot-modal>

    <!-- Modal Ver Imagem -->
    <woot-modal
      v-model:show="showImageModal"
      :on-close="
        () => {
          showImageModal = false;
          currentImageUrl = '';
        }
      "
      class="!max-w-4xl"
    >
      <woot-modal-header :header-title="$t('AI_ALERTS.BLOCKED_CONTENT')" />
      <div
        class="p-6 bg-slate-50 dark:bg-slate-900 flex justify-center items-center"
      >
        <img
          v-if="currentImageUrl"
          :src="currentImageUrl"
          :alt="$t('AI_ALERTS.HIDDEN_CONTENT')"
          class="max-w-full max-h-[70vh] rounded-md shadow-md border border-slate-200 dark:border-slate-700"
        />
      </div>
    </woot-modal>
    <ConfirmationModal
      ref="confirmDialog"
      :title="$t('AI_ALERTS.MARK_AS_RESOLVED')"
      :description="$t('AI_ALERTS.MARK_AS_RESOLVED_CONFIRMATION')"
      :confirm-label="$t('COMMON.OK')"
      :cancel-label="$t('COMMON.CANCEL')"
    />
  </div>
</template>

<style scoped>
/* Scoped styles removed in favor of Tailwind utility classes */
</style>
